<template>
  <div class="main-layout">
    <!-- 顶部导航栏 -->
    <header class="layout-header">
      <div class="header-left">
        <div class="logo" @click="router.push('/')" style="cursor: pointer;">
          <img src="../assets/logo_l1.png" alt="Logo" class="logo-image" />
        </div>
      </div>
      <div class="header-right">
        <!-- 菜单搜索按钮 -->
        <div class="menu-search-btn" @click="openSearch">
          <el-icon :size="16"><Search /></el-icon>
          <span>搜索菜单</span>
          <span class="search-shortcut">⌘K</span>
        </div>
        <!-- 全屏搜索蒙层 -->
        <teleport to="body">
          <div v-if="searchVisible" class="search-overlay" @click.self="closeSearch">
            <div class="search-panel">
              <div class="search-panel-header">
                <el-icon :size="18" class="search-icon"><Search /></el-icon>
                <input
                  v-model="searchKeyword"
                  autofocus
                  placeholder="输入关键词搜索菜单"
                  class="search-input"
                  @input="handleSearch"
                  @keydown.esc="closeSearch"
                />
                <button class="search-close-btn" @click="closeSearch">取消</button>
              </div>
              <div class="search-history" v-if="searchHistory.length > 0 && !searchKeyword">
                <div class="search-history-header">
                  <span class="search-history-title">搜索历史</span>
                  <span class="search-history-clear" @click="clearSearchHistory">清空</span>
                </div>
                <div class="search-history-tags">
                  <el-tag
                    v-for="(item, i) in searchHistory"
                    :key="i"
                    closable
                    size="small"
                    @click="selectHistory(item)"
                    @close="removeHistory(i)"
                  >
                    {{ item }}
                  </el-tag>
                </div>
              </div>
              <div class="search-panel-body" v-if="searchResults.length > 0">
                <div
                  v-for="item in searchResults"
                  :key="item.path"
                  class="search-item"
                  @click="handleSearchJump(item)"
                >
                  <span class="search-breadcrumb">
                    <span class="level1">{{ item.level1 }}</span>
                    <span class="sep">/</span>
                    <span v-if="item.level2" class="level2">{{ item.level2 }}<span class="sep">/</span></span>
                    <span class="level3">{{ item.label }}</span>
                  </span>
                </div>
              </div>
              <div class="search-panel-empty" v-else-if="searchKeyword && searchResults.length === 0">
                未找到匹配菜单
              </div>
              <div class="search-panel-hint" v-else>
                输入关键词搜索菜单
              </div>
            </div>
          </div>
        </teleport>
        <!-- 主题切换 -->
        <el-dropdown trigger="click" @command="handleThemeChange">
          <span class="theme-trigger">
            <el-icon :size="18"><Brush /></el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item v-for="(t, key) in themeList" :key="key" :command="key">
                <div class="theme-item">
                  <span class="theme-dot" :style="{ background: t.colors['--el-color-primary'] }"></span>
                  <span>{{ t.name }}</span>
                  <el-icon v-if="currentTheme === key" class="theme-check"><Check /></el-icon>
                </div>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <!-- 用户操作菜单 -->
        <el-dropdown trigger="click" @command="handleCommand">
          <span class="user-info">
            <el-avatar size="small" :src="userInfo?.avatar || Logo" />
            <span class="user-name">{{ userInfo?.name || '未知用户' }}</span>
            <el-icon class="el-icon--right"><arrow-down /></el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="profile">个人资料</el-dropdown-item>
              <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </header>

    <!-- 主体内容区 -->
    <div class="layout-main">
      <!-- 侧边菜单栏 - 分为两列 -->
      <aside class="layout-sidebar">
        <!-- 第一列：一级菜单 -->
        <div class="sidebar-column sidebar-column-first">
          <el-menu
            v-if="!menuStore.isLoading || menuStore.currentMenus.length > 0"
            :default-active="menuStore.activeFirstMenuItem"
            class="el-menu-vertical-demo first-level-menu"
            background-color="#FFFCF7"
            text-color="#6B5B3E"
            active-text-color="#C9A86C"
            @select="handleFirstLevelSelect"
          >
            <!-- 动态生成一级菜单 -->
            <template v-if="menuStore.currentMenus.length > 0">
              <template v-for="menu in menuStore.currentMenus">
                  <!-- 主菜单（无子菜单） -->
                  <el-menu-item v-if="menu.type === 'menu' || menu.type === 'item'" :key="menu.key" :index="menu.key">
                    <el-icon>
                      <component :is="getIconComponent(menu.icon)" />
                    </el-icon>
                    <template #title>{{ menu.label }}</template>
                  </el-menu-item>

                  <!-- 子菜单（有子菜单） -->
                  <el-menu-item v-else-if="menu.type === 'submenu'" :key="menu.key + '-sub'" :index="menu.key">
                    <el-icon>
                      <component :is="getIconComponent(menu.icon)" />
                    </el-icon>
                    <template #title>{{ menu.label }}</template>
                  </el-menu-item>
              </template>
            </template>
            <!-- 菜单为空时显示 -->
            <template v-else>
              <el-menu-item disabled>
                <el-icon><warning /></el-icon>
                <template #title>暂无菜单权限</template>
              </el-menu-item>
            </template>
          </el-menu>
          <!-- 菜单加载中 -->
          <div v-else class="menu-loading">
            <el-skeleton :rows="5" animated />
          </div>
        </div>

        <!-- 第二列：二级菜单 -->
        <div class="sidebar-column sidebar-column-second" v-if="selectedFirstMenu && selectedFirstMenu.children && selectedFirstMenu.children.length > 0">
          <el-menu
            :default-active="menuStore.activeMenuItem"
            class="el-menu-vertical-demo second-level-menu"
            background-color="#FFFCF7"
            text-color="#6B5B3E"
            active-text-color="#C9A86C"
            @select="handleMenuSelect"
          >
            <!-- 递归生成多级菜单 -->
            <!-- 一级菜单子项渲染 -->
            <!-- 子菜单（submenu类型） -->
            <template v-for="child in selectedFirstMenu.children.filter(item => item && item.type)">
              <!-- 二级菜单标题（仅用于显示，非交互元素） -->
              <div v-if="child.type === 'submenu' && child.children && child.children.length > 0" class="menu-group-title" :key="child.key + '-title'">
                {{ child.label }}
              </div>
              
              <!-- 二级菜单项（可点击） -->
              <el-menu-item
                v-if="child.type === 'submenu' && child.path"
                :key="child.key + '-item'"
                :index="child.path"
                @click="handleMenuItemClick(child.path)"
              >
                <!-- <el-icon><component :is="child.icon" /></el-icon> -->
                <template #title>{{ child.label }}</template>
              </el-menu-item>
              
              <!-- 三级菜单项渲染 -->
              <template v-if="child.type === 'submenu' && child.children && child.children.length > 0">
                <template v-for="grandChild in child.children.filter(item => item)">
                  <!-- 三级菜单标题（仅用于显示，非交互元素） -->
                  <div v-if="grandChild.type === 'submenu' && grandChild.children && grandChild.children.length > 0" class="menu-group-title-sub" :key="grandChild.key + '-title'">
                    {{ grandChild.label }}
                  </div>
                  
                  <!-- 三级菜单项（可点击） -->
                  <el-menu-item
                    v-if="grandChild.type === 'submenu' && grandChild.path"
                    :key="grandChild.key + '-item'"
                    :index="grandChild.path"
                    @click="handleMenuItemClick(grandChild.path)"
                  >
                    <template #title>{{ grandChild.label }}</template>
                  </el-menu-item>
                  
                  <!-- 四级菜单项渲染 -->
                  <template v-if="grandChild.type === 'submenu'">
                    <el-menu-item
                      v-for="greatGrandChild in (grandChild.children || []).filter(item => item && item.path)"
                      :key="greatGrandChild?.key"
                      :index="greatGrandChild.path"
                      @click="handleMenuItemClick(greatGrandChild.path)"
                    >
                      <!-- <el-icon><component :is="greatGrandChild.icon" /></el-icon> -->
                      <template #title>{{ greatGrandChild.label }}</template>
                    </el-menu-item>
                  </template>
                  
                  <!-- 三级菜单项（直接item类型） -->
                  <el-menu-item
                    v-else-if="grandChild.type === 'item'"
                    :key="grandChild.key"
                    :index="grandChild.path"
                    @click="handleMenuItemClick(grandChild.path)"
                  >
                    <!-- <el-icon><component :is="grandChild.icon" /></el-icon> -->
                    <template #title>{{ grandChild.label }}</template>
                  </el-menu-item>
                </template>
              </template>
              
              <!-- 二级菜单项（直接item类型） -->
              <el-menu-item
                v-else-if="child.type === 'item'"
                :key="child.key"
                :index="child.path"
                @click="handleMenuItemClick(child.path)"
              >
                <!-- <el-icon><component :is="child.icon" /></el-icon> -->
                <template #title>{{ child.label }}</template>
              </el-menu-item>
            </template>
          </el-menu>
        </div>
      </aside>

      <!-- 内容区域 -->
      <main class="layout-content">
        <router-view :key="routeKey" />
          <el-tooltip content="刷新当前页面" placement="left">
            <el-button class="global-refresh-btn" size="small" circle @click="refreshCurrentPage">
              <el-icon :size="14"><Refresh /></el-icon>
            </el-button>
          </el-tooltip>
      </main>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useMenuStore } from '../stores/menu';
import { ElMessage } from 'element-plus';
import { themes, loadTheme, getCurrentTheme } from '../utils/themeService';
import * as ElementPlusIconsVue from '@element-plus/icons-vue';

// 导入logo图片
import Logo from '../assets/logo.png';
import Logo_l from '../assets/logo_l1.png';

export default {
  name: 'MainLayout',
  components: {
    ...ElementPlusIconsVue
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const authStore = useAuthStore();
    const menuStore = useMenuStore();
    
    const userInfo = ref(authStore.userInfo);
    const selectedFirstMenu = ref(null);
    const themeList = ref(themes);
    const currentTheme = ref(getCurrentTheme());
    const routeKey = ref(0);
    const searchVisible = ref(false);
    const searchKeyword = ref('');
    const searchResults = ref([]);
    const searchInputRef = ref(null);
    const searchHistory = ref(JSON.parse(localStorage.getItem('menuSearchHistory') || '[]'));

    const saveSearchHistory = () => {
      localStorage.setItem('menuSearchHistory', JSON.stringify(searchHistory.value));
    };

    const addSearchHistory = (keyword) => {
      // 去重，移到最前
      const idx = searchHistory.value.indexOf(keyword);
      if (idx > -1) searchHistory.value.splice(idx, 1);
      searchHistory.value.unshift(keyword);
      // 最多5个
      if (searchHistory.value.length > 5) searchHistory.value = searchHistory.value.slice(0, 5);
      saveSearchHistory();
    };

    const selectHistory = (keyword) => {
      searchKeyword.value = keyword;
      handleSearch();
    };

    const removeHistory = (index) => {
      searchHistory.value.splice(index, 1);
      saveSearchHistory();
    };

    const clearSearchHistory = () => {
      searchHistory.value = [];
      saveSearchHistory();
    };

    const refreshCurrentPage = () => {
      routeKey.value++;
    };
    
    const handleThemeChange = (themeKey) => {
      currentTheme.value = themeKey;
      loadTheme(themeKey);
    };

    const openSearch = () => {
      searchVisible.value = true;
      searchKeyword.value = '';
      searchResults.value = [];
    };

    const closeSearch = () => {
      searchVisible.value = false;
      searchKeyword.value = '';
      searchResults.value = [];
    };

    // 搜索菜单
    const handleSearch = () => {
      const keyword = searchKeyword.value.trim().toLowerCase();
      if (!keyword) {
        searchResults.value = [];
        return;
      }
      const results = [];
      const flatMenus = menuStore.menus || [];
      flatMenus.forEach(level1 => {
        (level1.children || []).forEach(level2 => {
          (level2.children || []).forEach(level3 => {
            if (level3.label && level3.label.toLowerCase().includes(keyword)) {
              results.push({ level1: level1.label, level2: level2.label || level1.label, label: level3.label, path: level3.path || '', type: level3.type });
            }
          });
        });
      });
      searchResults.value = results.slice(0, 20);
    };

    // 搜索跳转
    const handleSearchJump = (item) => {
      if (!item.path) return;
      addSearchHistory(item.label);
      searchVisible.value = false;
      searchKeyword.value = '';
      searchResults.value = [];
      router.push(item.path);
    };
    
    // 键盘快捷键监听
    const handleKeydown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openSearch();
      }
    };

    // 初始化菜单
    const initMenus = async () => {
      try {
        await menuStore.initMenus();
        // 初始化时，根据当前路由选择对应的一级菜单
        updateSelectedFirstMenu();
      } catch (error) {
        ElMessage.error('初始化菜单失败');
        console.error('初始化菜单失败:', error);
      }
    };

    // 递归检查菜单项是否包含当前路径
    const menuContainsPath = (menu, path) => {
      // 检查menu是否存在
      if (!menu) {
        return false;
      }
      // 检查当前菜单项 (item 或 submenu 只要有 path 都匹配)
      if ((menu.type === 'item' || menu.type === 'submenu') && menu.path === path) {
        return true;
      }
      // 递归检查子菜单
      if (menu.type === 'submenu' && menu.children && menu.children.length > 0) {
        return menu.children.some(child => menuContainsPath(child, path));
      }
      return false;
    };

    // 根据当前路由更新选中的一级菜单
    const updateSelectedFirstMenu = () => {
      const currentPath = route.path;
      // 查找当前路由对应的一级菜单
      const firstMenu = menuStore.currentMenus.find(menu => {
        if (menu.type === 'menu') {
          return menu.path === currentPath;
        } else if (menu.type === 'submenu' && menu.children) {
          // 递归检查所有层级的子菜单
          return menu.children.some(child => menuContainsPath(child, currentPath));
        }
        return false;
      });
      
      if (firstMenu) {
        selectedFirstMenu.value = firstMenu;
        menuStore.setActiveFirstMenuItem(firstMenu.key);
      } else if (menuStore.currentMenus.length > 0) {
        // 默认选择第一个菜单
        selectedFirstMenu.value = menuStore.currentMenus[0];
        menuStore.setActiveFirstMenuItem(menuStore.currentMenus[0].key);
      }
    };

    // 递归查找第一个可跳转的菜单项路径
    const findFirstNavigablePath = (menus) => {
      console.log('findFirstNavigablePath called with:', menus);
      
      if (!menus || menus.length === 0) {
        console.log('No menus provided, returning null');
        return null;
      }
      
      for (const menu of menus) {
        // 检查menu是否存在
        if (!menu) {
          continue;
        }
        
        console.log('Checking menu:', menu.label, 'type:', menu.type, 'path:', menu.path);
        
        // 检查当前菜单是否是可跳转的item类型
        if (menu.type === 'item' && menu.path) {
          console.log('Found navigable item:', menu.label, 'path:', menu.path);
          return menu.path;
        }
        // 检查当前菜单是否是可跳转的submenu类型
        else if (menu.type === 'submenu' && menu.path) {
          console.log('Found navigable submenu:', menu.label, 'path:', menu.path);
          return menu.path;
        }
        // 如果是submenu类型且有子菜单，递归查找其子菜单
        else if (menu.type === 'submenu' && menu.children && menu.children.length > 0) {
          console.log('Checking submenu children:', menu.children.length);
          
          // 递归查找所有层级的子菜单
          for (const child of menu.children) {
            if (child) {
              // 检查子菜单是否是可跳转的
              if ((child.type === 'item' || child.type === 'submenu') && child.path) {
                console.log('Found navigable child:', child.label, 'path:', child.path);
                return child.path;
              }
              // 如果子菜单还有子菜单，继续递归
              else if (child.type === 'submenu' && child.children && child.children.length > 0) {
                const path = findFirstNavigablePath(child.children);
                if (path) {
                  return path;
                }
              }
            }
          }
        }
        // 如果是menu类型（一级菜单），直接返回其path
        else if (menu.type === 'menu' && menu.path) {
          console.log('Found menu type with path:', menu.label, 'path:', menu.path);
          return menu.path;
        }
      }
      
      console.log('No navigable path found in menus');
      return null;
    };

    // 处理一级菜单选择
    const handleFirstLevelSelect = (key) => {
      // 调试：输出所有当前菜单数据
      console.log('All current menus:', menuStore.currentMenus);
      console.log('Clicked menu key:', key);
      
      const menu = menuStore.currentMenus.find(m => m.key === key);
      if (menu) {
        selectedFirstMenu.value = menu;
        menuStore.setActiveFirstMenuItem(key);
        
        // 调试：输出选中的菜单信息
        console.log('Selected menu:', menu);
        

        
        // 如果是无子菜单的一级菜单，直接跳转
        if ((menu.type === 'menu' || menu.type === 'item') && menu.path) {
          console.log('Using menu path:', menu.path);
          // 添加延迟确保组件状态稳定后再导航，避免卸载时的错误
          setTimeout(() => {
            router.push(menu.path);
          }, 0);
        }
        // 如果有子菜单，递归查找第一个可跳转的菜单项
        else if (menu.type === 'submenu' && menu.children && menu.children.length > 0) {
          // 调试：输出菜单数据
          console.log('Menu children:', menu.children);
          
          // 使用递归查找第一个可跳转的路径
          const targetPath = findFirstNavigablePath(menu.children);
          // 调试：输出查找结果
          console.log('Target path found:', targetPath);
          
          // 如果找到可跳转的路径，进行跳转
          if (targetPath) {
            // 添加延迟确保组件状态稳定后再导航，避免卸载时的错误
            setTimeout(() => {
              router.push(targetPath);
            }, 0);
          } else {
            // 兜底方案：如果没有找到可跳转的路径
            console.log('No navigable path found in menu:', menu.label);
            
            // 显示提示信息，告知用户该菜单下暂无可用功能
            ElMessage.warning('该菜单下暂无可用功能');
            
            // 不进行跳转，保持在当前页面
            // 或者跳转到首页作为安全的默认选项
            // 添加延迟确保组件状态稳定后再导航，避免卸载时的错误
            setTimeout(() => {
              // 检查是否有其他可用的菜单
              if (menuStore.currentMenus.length > 0) {
                // 查找第一个有可跳转路径的菜单
                const firstNavigablePath = findFirstNavigablePath(menuStore.currentMenus);
                if (firstNavigablePath) {
                  console.log('Using first navigable path from all menus:', firstNavigablePath);
                  router.push(firstNavigablePath);
                } else {
                  // 如果所有菜单都没有可跳转路径，跳转到首页
                  console.log('No navigable path found in any menu, redirecting to home');
                  router.push('/');
                }
              } else {
                // 如果没有菜单，跳转到首页
                router.push('/');
              }
            }, 0);
          }
        }
      } else {
        // 调试：未找到菜单
        console.log('Menu not found for key:', key);
        ElMessage.error('菜单未找到');
      }
    };

    // 处理菜单选择（保留以便el-menu @select事件使用）
    const handleMenuSelect = (index) => {
      menuStore.setActiveMenuItem(index);
    };
    
    // 处理菜单项点击
    const handleMenuItemClick = (path) => {
      menuStore.setActiveMenuItem(path);
      // 添加延迟确保组件状态稳定后再导航，避免卸载时的错误
      setTimeout(() => {
        router.push(path);
      }, 0);
    };

    // 获取图标组件
    const getIconComponent = (iconName) => {
      // 定义可用图标映射表
      const iconMap = {
        'home': ElementPlusIconsVue.House,
        'dashboard': ElementPlusIconsVue.DataAnalysis,
        'credit-card': ElementPlusIconsVue.CreditCard,
        'star': ElementPlusIconsVue.Star,
        'setting': ElementPlusIconsVue.Setting,
        'list': ElementPlusIconsVue.List,
        'present': ElementPlusIconsVue.Present,
        'office-building': ElementPlusIconsVue.OfficeBuilding,
        'calendar': ElementPlusIconsVue.Calendar,
        'camera': ElementPlusIconsVue.Camera,
        'brush': ElementPlusIconsVue.Brush,
        'shopping-cart': ElementPlusIconsVue.ShoppingCart,
        'cloud': ElementPlusIconsVue.Cloudy,
        'help': ElementPlusIconsVue.Help,
        'goods': ElementPlusIconsVue.Goods,
        'management': ElementPlusIconsVue.Management,
        'home-filled': ElementPlusIconsVue.House,
        'shopping-bag': ElementPlusIconsVue.ShoppingBag,
        'bar-chart': ElementPlusIconsVue.TrendCharts
      };
      
      // 返回对应的图标组件，如果找不到则返回默认图标
      return iconMap[iconName] || ElementPlusIconsVue.House;
    };

    // 处理用户菜单命令
    const handleCommand = (command) => {
      switch (command) {
        case 'profile':
          router.push('/profile');
          break;
        case 'settings':
          ElMessage.info('设置功能开发中');
          break;
        case 'logout':
          handleLogout();
          break;
        default:
          break;
      }
    };

    // 处理退出登录
    const handleLogout = async () => {
      try {
        await authStore.logout();
        // 重置菜单状态
        menuStore.reset();
        selectedFirstMenu.value = null;
        ElMessage.success('退出成功');
        router.push('/login');
      } catch (error) {
        ElMessage.error('退出失败');
      }
    };

    // 监听路由变化，更新激活菜单和选中的一级菜单
    watch(
      () => route.path,
      (newPath) => {
        menuStore.setActiveMenuItem(newPath);
        updateSelectedFirstMenu();
      },
      { immediate: true }
    );

    // 监听用户信息变化，重新加载菜单
    watch(
      () => authStore.userInfo,
      (newUserInfo) => {
        userInfo.value = newUserInfo;
        // 用户信息变化时，重新加载菜单
        initMenus();
      }
    );

    // 组件挂载时初始化
    onMounted(() => {
      window.addEventListener('keydown', handleKeydown);
      // 初始化authStore，从localStorage获取用户信息
      authStore.initState();
      userInfo.value = authStore.userInfo;
      // 初始化菜单
      initMenus();
      // 设置当前激活菜单
      menuStore.setActiveMenuItem(route.path);
    });

    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeydown);
    });
    
    return {
        userInfo,
        menuStore,
        selectedFirstMenu,
        handleFirstLevelSelect,
        handleMenuSelect,
        handleCommand,
        handleLogout,
        handleMenuItemClick,
        getIconComponent,
        Logo,
        Logo_l,
        router,
        themeList,
        currentTheme,
        handleThemeChange,
        routeKey,
        refreshCurrentPage,
        searchVisible,
        searchKeyword,
        searchResults,
        searchInputRef,
        handleSearch,
        handleSearchJump,
        closeSearch,
        openSearch,
        searchHistory,
        selectHistory,
        removeHistory,
        clearSearchHistory
    };
  }
};
</script>

<style scoped>
.main-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

/* 顶部导航栏 */
.layout-header {
  height: 60px;
  background: linear-gradient(135deg, var(--card-bg) 0%, var(--primary-light-9) 100%);
  border-bottom: 1px solid var(--border-color);
  box-shadow: 0 1px 4px rgba(44, 62, 80, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
}

.header-left .logo {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  color: var(--primary-color);
}

.logo-image {
  height: 40px;
  width: auto;
  margin-right: 10px;
  vertical-align: middle;
}

.logo-text {
  vertical-align: middle;
}

.header-right {
  display: flex;
  align-items: center;
}

.user-info {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.3s;
  color: var(--primary-color);
}

.user-info:hover {
  background-color: var(--primary-light-9);
}

/* 主题切换按钮 */
.theme-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.3s;
  color: var(--el-color-primary);
  margin-right: 4px;
}
.theme-trigger:hover {
  background-color: var(--el-color-primary-light-9);
}
.theme-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
}
.theme-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid #e0e0e0;
  flex-shrink: 0;
}
.theme-check {
  margin-left: auto;
  color: var(--el-color-primary);
  font-size: 14px;
}

.user-info .user-name {
  margin: 0 8px;
  color: var(--primary-color);
}

/* 主体内容区 */
.layout-main {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* 侧边菜单栏 - 两列布局 */
.layout-sidebar {
  background-color: var(--primary-light-8);
  color: var(--text-color);
  display: flex;
  transition: all 0.3s ease;
  border-right: 1px solid var(--border-color);
  display: flex;
  overflow: hidden;
}

/* 侧边栏列样式 */
.sidebar-column {
  height: calc(100vh - 60px); /* 减去顶部导航栏高度 */
  overflow-y: auto;
  transition: all 0.3s ease;
}

/* 第一列：一级菜单 */
.sidebar-column-first {
  width: 160px; /* 恢复第一列宽度 */
  background-color: var(--card-bg); /* 白色背景 */
  border-right: 1px solid var(--border-color);
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
}

/* 第二列：二级菜单 */
.sidebar-column-second {
  width: 130px; /* 恢复第二列宽度 */
  background-color: var(--card-bg); /* 白色背景 */
  border-right: 1px solid var(--border-color);
}

/* 一级菜单样式 */
.first-level-menu {
  height: 100%;
  background-color: inherit;
  color: var(--text-color);
}

/* 二级菜单样式 */
.second-level-menu {
  height: 100%;
  background-color: var(--card-bg);
  color: var(--text-color);
}

/* 一级菜单和二级菜单的基础样式 */
.layout-sidebar .el-menu {
  font-size: 14px; /* 调整字体大小，提高可读性 */
  font-weight: 400;
  border-right: none;
}

.layout-sidebar .el-menu-item {
  height: 48px; /* 优化高度，提高点击区域 */
  line-height: 48px; /* 调整行高 */
  padding: 0 18px; /* 优化内边距，提高可读性 */
  font-size: 14px; /* 统一字体大小 */
  font-weight: 400;
  border-right: 3px solid transparent;
  transition: all 0.3s ease; /* 添加平滑过渡效果 */
}

/* 第一列菜单文字颜色 */
.sidebar-column-first .el-menu-item {
  color: var(--text-color);
  margin-bottom: 4px;
  border-radius: 6px 0 0 6px;
}

/* 第二列菜单文字颜色 */
.sidebar-column-second .el-menu-item {
  color: var(--text-color);
}

/* 一级菜单的特殊样式 */
.first-level-menu .el-menu-item {
  border-right: 3px solid transparent;
  height: 52px; /* 优化一级菜单高度 */
  line-height: 52px; /* 调整行高 */
}

.first-level-menu .el-menu-item:hover {
  background-color: var(--primary-light-9); /* 浅灰蓝色背景 */
  color: var(--primary-color); /* 藏青色文字 */
  border-right-color: var(--primary-color); /* 藏青色边框 */
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.15);
  transform: translateX(2px);
}

.first-level-menu .el-menu-item.is-active {
  background-color: var(--primary-light-9); /* 浅灰蓝色背景 */
  color: var(--primary-color); /* 藏青色文字 */
  font-weight: 600;
  border-right-color: var(--primary-color); /* 藏青色边框 */
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.2);
  transform: translateX(2px);
}

/* 二级菜单的特殊样式 */
.second-level-menu .el-menu-item {
  background-color: transparent;
  border-right: none;
  padding: 0 20px; /* 优化内边距 */
  width: 100%;
  text-align: left; /* 左侧对齐，提高可读性 */
  border-radius: 4px; /* 圆角设计，增强现代感 */
  margin: 2px 8px; /* 适当的间距 */
}

.second-level-menu .el-menu-item:hover {
  background-color: var(--primary-light-9);
  color: var(--primary-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transform: translateX(2px);
}

.second-level-menu .el-menu-item.is-active {
  background-color: var(--primary-light-9);
  color: var(--primary-color);
  font-weight: 600;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transform: translateX(2px);
}

/* 菜单分组标题样式 */
.menu-group-title {
  padding: 15px 10px 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: transparent;
  text-align: left;
  width: 100%;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-light);
}

/* 调整侧边栏图标大小和间距 */
.layout-sidebar .el-icon {
  margin-right: 10px;
  font-size: 16px;
  color: var(--text-light);
  transition: color 0.3s ease;
}

/* 第一列图标颜色 */
.sidebar-column-first .el-icon {
  color: var(--text-light);
}

/* 第二列图标颜色 */
.sidebar-column-second .el-icon {
  color: var(--text-light);
}

.layout-sidebar .el-menu-item:hover .el-icon,
.layout-sidebar .el-menu-item.is-active .el-icon {
  color: var(--primary-color);
}

/* 菜单加载状态样式 */
.menu-loading {
  padding: 20px;
  height: 100%;
}

.sidebar-column-first .menu-loading {
  background-color: #FFFFFF;
}

.sidebar-column-second .menu-loading {
  background-color: #FFFFFF;
}

/* 无二级菜单时的样式 */
.no-submenu {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #7F8C8D;
  background-color: #FFFFFF;
}

.no-submenu p {
  margin: 0;
  font-size: 14px;
}

.menu-loading .el-skeleton {
  background-color: #F0F4F8;
}

.menu-loading .el-skeleton__row {
  margin-bottom: 20px;
}

.menu-loading .el-skeleton__row:last-child {
  margin-bottom: 0;
}

/* 内容区域顶部刷新按钮 */
.global-refresh-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 999;
  border: 1px solid #d9d9d9;
  background: rgba(255,255,255,0.9);
  color: #909399;
  width: 32px;
  height: 32px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  backdrop-filter: blur(4px);
}
.global-refresh-btn:hover {
  color: #409eff;
  border-color: #409eff;
  background: rgba(255,255,255,1);
  box-shadow: 0 4px 12px rgba(64,158,255,0.2);
}

/* 内容区域 */
.layout-content {
  flex: 1;
  padding: 20px;
  background-color: var(--bg-color);
  color: var(--text-color);
  overflow-y: auto;
}

/* 下拉菜单样式 */
.el-dropdown-menu {
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

.el-dropdown-item {
  color: var(--text-color);
}

.el-dropdown-item:hover {
  background-color: var(--primary-light-9);
  color: var(--primary-color);
}

/* 内容区域组件样式 */
.layout-content .el-card,
.layout-content :deep(.el-card) {
  border: 1px solid var(--border-color) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  border-radius: 12px !important;
  background-color: var(--card-bg) !important;
  overflow: hidden !important;
}

.layout-content .el-card__header,
.layout-content :deep(.el-card__header) {
  border-bottom: 1px solid var(--border-color) !important;
  background-color: var(--card-header-bg) !important;
  border-radius: 12px 12px 0 0 !important;
}

/* 表单卡片样式 */
.el-form {
  background-color: var(--card-bg);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 表格样式优化 */
.el-table {
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
}

.el-table__inner-wrapper {
  border-radius: 12px;
}

.el-table__header-wrapper {
  background-color: var(--card-header-bg);
  border-bottom: 1px solid var(--border-color);
}

.el-table__body-wrapper {
  background-color: var(--card-bg);
}

.el-table__row:hover {
  background-color: var(--primary-light-9);
}

/* 对话框样式 */
.el-dialog {
  border-radius: 12px;
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.el-dialog__header {
  background-color: var(--card-header-bg);
  border-bottom: 1px solid var(--border-color);
  border-radius: 12px 12px 0 0;
}

/* 标签页样式 */
.el-tabs {
  background-color: var(--card-bg);
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.el-tabs__header {
  margin-bottom: 16px;
}

.el-tabs__nav-wrap {
  background-color: var(--card-bg);
}

/* 分页样式 */
.el-pagination {
  background-color: var(--card-bg);
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
}
</style>
<style>
/* 菜单搜索按钮 */
.menu-search-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid var(--border-color);
  background: var(--card-bg);
  color: var(--text-light);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}
.menu-search-btn:hover {
  border-color: var(--el-color-primary);
  color: var(--el-color-primary);
}
.search-shortcut {
  font-size: 11px;
  padding: 1px 5px;
  border-radius: 3px;
  background: var(--border-color);
  color: var(--text-light);
  line-height: 1.4;
  margin-left: 2px;
}

/* 全屏蒙层 */
.search-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  padding-top: 100px;
}
.search-panel {
  position: relative;
  width: 620px;
  margin: 0 auto;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  overflow: hidden;
}
.search-panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-color);
}
.search-icon {
  color: var(--text-light);
  flex-shrink: 0;
}
.search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 18px;
  color: var(--text-color);
  line-height: 1;
}
.search-input::placeholder {
  color: var(--text-light);
}
.search-close-btn {
  padding: 6px 16px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: transparent;
  color: var(--text-light);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}
.search-close-btn:hover {
  border-color: var(--el-color-primary);
  color: var(--el-color-primary);
}
.search-panel-body {
  max-height: 420px;
  overflow-y: auto;
  padding: 12px 16px;
}
.search-panel-body::-webkit-scrollbar {
  width: 4px;
}
.search-panel-body::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 4px;
}
.search-item {
  padding: 14px 16px;
  cursor: pointer;
  border-radius: 10px;
  transition: background 0.15s;
}
.search-item:hover {
  background: var(--el-color-primary-light-9);
}
.search-breadcrumb {
  display: flex;
  align-items: center;
  gap: 0;
  line-height: 2;
}
.search-breadcrumb .sep {
  margin: 0 10px;
  color: var(--text-light);
  font-size: 13px;
}
.search-breadcrumb .level1,
.search-breadcrumb .level2 {
  color: var(--text-light);
  font-size: 13px;
}
.search-breadcrumb .level3 {
  font-weight: 600;
  font-size: 15px;
  color: var(--text-color);
}
.search-item:hover .level3 {
  color: var(--el-color-primary);
}
.search-panel-empty,
.search-panel-hint {
  text-align: center;
  color: var(--text-light);
  font-size: 15px;
  padding: 32px 0;
}

/* 搜索历史 */
.search-history {
  padding: 10px 16px;
  border-bottom: 1px solid var(--border-color);
}
.search-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.search-history-title {
  font-size: 12px;
  color: var(--text-light);
}
.search-history-clear {
  font-size: 12px;
  color: var(--el-color-primary);
  cursor: pointer;
}
.search-history-clear:hover {
  opacity: 0.8;
}
.search-history-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.search-history-tags .el-tag {
  cursor: pointer;
  border-radius: 4px;
}

/* 页面面包屑标题 */
.breadcrumb-level {
  font-size: 15px;
  font-weight: 400;
  color: #909399;
}
.breadcrumb-current {
  font-weight: 600;
  color: #000000;
}
.breadcrumb-sep {
  font-size: 15px;
  color: #C0C4CC;
  margin: 0 4px;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e4e7ed;
  padding: 4px 0 16px;
  margin-bottom: 20px;
}
</style>
